import { ReactElement, useState, useEffect } from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { CorrectIcon } from 'src/v3/core/icons'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveLinkedService,
  fetchQuizAnalytics,
  selectQuizAnalytics,
  selectClasses,
  selectClassesStatus,
  fetchClasses,
  selectSections,
  selectSectionsStatus,
  fetchSections,
} from 'src/v3/manage/manage.slice'

interface QuizAnalyticsProps {
  startDate: string
  endDate: string
}

export function QuizAnalytics({
  startDate,
  endDate,
}: QuizAnalyticsProps): ReactElement {
  let dispatch = useAppDispatch()

  let [section, setSection] = useState<string>('')
  let [view, setView] = useState<string>('List')
  let [quiz, setQuiz] = useState<string>('')
  let [question, setQuestion] = useState<string>('')
  let [loadingAnalytics, setLoadingAnalytics] = useState<boolean>(false)

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let quizAnalytics = useAppSelector(selectQuizAnalytics)
  let classes = useAppSelector(selectClasses)
  let classesStatus = useAppSelector(selectClassesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    setSection('')
    setQuiz('')
    setQuestion('')
    if (activeLinkedService !== 'unselected') {
      setLoadingAnalytics(true)
      dispatch(
        fetchQuizAnalytics({
          linkedServiceID: activeLinkedService,
          startDate,
          endDate,
        }),
      ).then(() => setLoadingAnalytics(false))
    }
  }, [activeLinkedService, startDate, endDate])

  let getColor = (rate: number) => {
    if (rate >= 70) {
      return 'success'
    }
    if (rate >= 50) {
      return 'warning'
    }
    return 'error'
  }

  let filteredClasses = classes.filter((c) => c.sectionID === section)

  return (
    <Card variant="outlined" sx={{ my: 0, p: 2, minWidth: 300 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'revert',
          textAlign: 'center',
        }}
      >
        Quiz Analytics
      </Typography>
      <div style={{ marginTop: '5px' }}>
        <Typography
          color="text.secondary"
          sx={{
            textAlign: 'center',
            marginBottom: '2px',
          }}
        >
          Data View
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {['Graph', 'List'].map((v) => (
            <Chip
              key={v}
              color="primary"
              label={v}
              onClick={() => setView(v)}
              variant={view === v ? 'filled' : 'outlined'}
              style={{ cursor: 'pointer' }}
              disabled={v === 'Graph'}
            />
          ))}
        </Stack>
      </div>
      {loadingAnalytics && <LinearProgress sx={{ my: 1 }} />}
      {quizAnalytics[activeLinkedService] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: '10px' }}>
          <FormControl sx={{ minWidth: 200, my: '2px' }}>
            <InputLabel size="small" sx={{ paddingRight: 2 }}>
              Section
            </InputLabel>
            <Select
              value={section}
              label="Section"
              variant="outlined"
              size="small"
              onChange={(event) => {
                setQuestion('')
                setQuiz('')
                setSection(`${event.target.value}`)
              }}
            >
              <MenuItem key="none" value={''}>
                None
              </MenuItem>
              {sections
                // TODO: When Section is in the manage slice, this should not be <any>
                .filter((s: any) =>
                  quizAnalytics[activeLinkedService]?.quizRate.some((qr) =>
                    classes.some(
                      (c) => c.sectionID === s.id && c.name === qr.className,
                    ),
                  ),
                )
                // TODO: When Section is in the manage slice, this should not be <any>
                .map((s: any) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {quizAnalytics[activeLinkedService] && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          <Box sx={{ flex: '1 0 300px' }}>
            <Typography variant="h6" align="center">
              Select Quiz
            </Typography>
            {section ? (
              <List
                disablePadding
                sx={{
                  margin: 'auto',
                  maxHeight: 400,
                  overflow: 'scroll',
                  borderStyle: 'solid',
                  borderWidth: '1px',
                  borderColor: 'divider',
                }}
              >
                {quizAnalytics[activeLinkedService]?.quizRate
                  .filter((qr) =>
                    filteredClasses.some((c) => c.name === qr.className),
                  )
                  .map((qr, i) => (
                    <>
                      {!!i && <Divider />}
                      <ListItemButton
                        key={i}
                        onClick={() => {
                          setQuiz(qr.quizId === quiz ? '' : qr.quizId)
                          setQuestion('')
                        }}
                        dense
                      >
                        <ListItemIcon sx={{ p: 0, width: '40px' }}>
                          <Checkbox
                            edge="start"
                            checked={quiz === qr.quizId}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography>{qr.className}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={qr.passedRate}
                            color={getColor(qr.passedRate)}
                          />
                          <Typography
                            align="center"
                            color="text.secondary"
                            sx={{
                              fontSize: 14,
                            }}
                          >
                            {(Math.round(qr.passedRate * 100) / 100).toFixed(2)}
                            % Passed ({qr.passedCount}/{qr.totalAttempts}{' '}
                            Attempts)
                          </Typography>
                        </ListItemText>
                      </ListItemButton>
                    </>
                  ))}
              </List>
            ) : (
              <>
                <Divider />
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ mt: '3px' }}
                >
                  Please select a section.
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ flex: '1 0 300px' }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'revert',
                textAlign: 'center',
              }}
            >
              Select Question
            </Typography>
            {quiz ? (
              <List
                disablePadding
                sx={{
                  margin: 'auto',
                  maxHeight: 400,
                  overflow: 'scroll',
                  borderStyle: 'solid',
                  borderWidth: '1px',
                  borderColor: 'divider',
                }}
              >
                {quizAnalytics[activeLinkedService]?.questionRate
                  .filter((qr) => qr.quizId === quiz)
                  .map((qr, i) => (
                    <>
                      {!!i && <Divider />}
                      <ListItemButton
                        key={i}
                        onClick={() =>
                          setQuestion(
                            qr.questionText === question ? '' : qr.questionText,
                          )
                        }
                        dense
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={
                              quiz === qr.quizId && question === qr.questionText
                            }
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography>{qr.questionText}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={qr.passedRate}
                            color={getColor(qr.passedRate)}
                          />
                          <Typography
                            align="center"
                            color="text.secondary"
                            sx={{
                              fontSize: 14,
                            }}
                          >
                            {(Math.round(qr.passedRate * 100) / 100).toFixed(2)}
                            % Passed ({qr.passedCount}/{qr.totalAttempts}{' '}
                            Attempts)
                          </Typography>
                        </ListItemText>
                      </ListItemButton>
                    </>
                  ))}
              </List>
            ) : (
              <>
                <Divider />
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ mt: '3px' }}
                >
                  Please select a quiz.
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ flex: '1 0 300px' }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'revert',
                textAlign: 'center',
              }}
            >
              Question Breakdown
            </Typography>
            {question ? (
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  borderStyle: 'solid',
                  borderWidth: '1px',
                  borderColor: 'divider',
                  maxHeight: 400,
                  overflow: 'scroll',
                }}
              >
                <Typography sx={{ mb: 1 }}>{question}</Typography>
                {quizAnalytics[activeLinkedService]?.questionBreakdown
                  .filter(
                    (qb) => qb.quizID === quiz && qb.questionText === question,
                  )
                  .map((qb) => (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          mb: '-1px',
                        }}
                      >
                        {
                          qb.isCorrect === 'true' ? (
                            <CorrectIcon color="success" />
                          ) : (
                            <></>
                          ) // : <IncorrectIcon color='error' />
                        }
                        <Typography>{qb.choiceText}</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          columnGap: 1,
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <LinearProgress
                            variant="determinate"
                            value={qb.percentChosen}
                            color={
                              qb.isCorrect === 'true' ? 'success' : 'error'
                            }
                          />
                        </Box>
                        <Box sx={{ width: 'fit-content' }}>
                          <Typography variant="body2" color="text.secondary">
                            {(Math.round(qb.percentChosen * 100) / 100).toFixed(
                              2,
                            )}
                            %
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ))}
              </Box>
            ) : (
              <>
                <Divider />
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ mt: '3px' }}
                >
                  Please select a question.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}
    </Card>
  )
}

export default QuizAnalytics
